import React, { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Megaphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmt } from "@/lib/api";

const Tab = ({ to, icon: Icon, children }) => (
  <NavLink
    to={to}
    end
    data-testid={`admin-nav-${children.toString().toLowerCase()}`}
    className={({ isActive }) =>
      `flex items-center gap-2 px-4 py-2.5 label-mono text-xs border-l-2 transition ${
        isActive ? "border-cyan-400 bg-cyan-500/5 text-cyan-400" : "border-transparent text-slate-400 hover:text-white"
      }`
    }
  >
    <Icon size={14} /> {children}
  </NavLink>
);

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") api.get("/admin/stats").then((r) => setStats(r.data));
  }, [user]);

  if (loading) return <div className="py-20 text-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/admin" }} />;
  if (user.role !== "admin") return <Navigate to="/" />;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-layout">
      <div className="label-mono text-cyan-400 mb-2">/ CONTROL ROOM</div>
      <h1 className="text-4xl font-medium tracking-tight">Admin Dashboard</h1>

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/5 border border-white/5">
          {[
            ["Revenue", fmt(stats.revenue)],
            ["Paid Orders", stats.paid_orders],
            ["Total Orders", stats.total_orders],
            ["Products", stats.products_count],
            ["Customers", stats.users_count],
          ].map(([l, v]) => (
            <div key={l} className="bg-slate-950 p-5">
              <div className="label-mono text-slate-500">{l}</div>
              <div className="font-mono text-2xl text-cyan-400 mt-2">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 grid md:grid-cols-[200px_1fr] gap-8">
        <nav className="border-l border-white/5 sticky top-24 self-start">
          <Tab to="/admin" icon={LayoutDashboard}>Overview</Tab>
          <Tab to="/admin/products" icon={Package}>Products</Tab>
          <Tab to="/admin/orders" icon={ShoppingBag}>Orders</Tab>
          <Tab to="/admin/marketing" icon={Megaphone}>Marketing</Tab>
        </nav>
        <div>
          <Outlet context={{ stats }} />
        </div>
      </div>
    </div>
  );
}
